import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row, Skeleton, Space, Switch, Tooltip, Typography, message } from 'antd';
import { FileTextOutlined, FilterOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ListView from './ListView.jsx';
import SearchFieldControl from './searchBuilder/SearchFieldControl.jsx';
import BooleanQueryField from './searchBuilder/BooleanQueryField.jsx';
import { BOOLEAN_PARSEABLE_API_KEYS, isBooleanParsable, validateBooleanQuery } from './searchBuilder/booleanQueryUtils';
import { buildBooleanRequest } from './searchBuilder/buildBooleanRequest';
import { buildTraditionalParams } from './searchBuilder/buildTraditionalParams';
import { buildFilterConditions } from './searchBuilder/buildFilterConditions';
import { safeNavTarget } from '../utils/afterSubmitNav';
import { getCustomForms } from '../services/adminApi';
import { searchModuleData } from '../services/moduleDataApi';
import { searchBoolean } from '../services/booleanSearchApi';
import {
  buildSourceTabs,
  getAvailableSourceActions,
  getSearchSources,
  getSourceActionInstanceKey,
  resolveSearchSource,
  runSourceAction,
  sourceActionAppliesToRecord,
} from '../services/sourceProviderActions';
import '../styles/SearchBuilderV1.css';

const { Text } = Typography;

// Picking a source is not itself a search criterion — searching on it alone
// would return the entire collection.
const SOURCE_ONLY_FIELDS = new Set(['selectedSource', 'source', 'sourceType', 'scope']);

const EMPTY_RESULTS = {
  searched: false, rows: [], total: 0, fields: [], actions: [], actionRules: [], loading: false,
};

function extractGroups(schema) {
  if (Array.isArray(schema)) return schema;
  if (Array.isArray(schema?.groups)) return schema.groups;
  if (Array.isArray(schema?.data?.groups)) return schema.data.groups;
  return [];
}

function truthy(value) {
  return value === true || value === 1 || value === '1';
}

// A locked checkbox/radio option (admin-configured — off by default, see
// FormGroupsSection's Options/Values editor) must be selected from the very
// first render, not just visually via the registry renderer's own defensive
// merge — otherwise submitting without ever touching the control would omit
// it from the request entirely. Radio only allows one selection, so its
// locked default is a single value; checkbox's is every locked option.
function lockedInitialValue(field) {
  const options = Array.isArray(field.options) ? field.options : [];
  const locked = options.filter((option) => option.locked).map((option) => option.value);
  if (!locked.length) return undefined;
  return field.type === 'radio' ? locked[0] : locked;
}

function hasCriteria(values) {
  return Object.entries(values ?? {}).some(([key, value]) => {
    if (SOURCE_ONLY_FIELDS.has(key)) return false;
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });
}

export default function SearchBuilderV1({
  moduleName,
  action,
  formType = 'search',
  title,
  migrationJobId,
  searchActionContext,
  onBack,
}) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const [searchMode, setSearchMode] = useState('traditional');
  const [selectedSource, setSelectedSource] = useState('internal');
  const [fieldErrors, setFieldErrors] = useState({});
  const [tabs, setTabs] = useState([]);
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [lastValues, setLastValues] = useState(null);
  const [sourceActionState, setSourceActionState] = useState({});
  const [formValues, setFormValues] = useState({});

  // Every state write happens in a promise callback, and a config that resolves
  // after the props changed (or after unmount) is discarded rather than
  // overwriting the newer one.
  useEffect(() => {
    if (!moduleName) return undefined;
    let cancelled = false;

    getCustomForms(moduleName, formType, action)
      .then((data) => {
        if (cancelled) return;
        setSchema(Array.isArray(data) ? data[0] ?? null : data ?? null);
        setLoadError('');
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error?.message || 'Failed to load the search form configuration.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [moduleName, formType, action, reloadToken]);

  const retry = () => { setLoading(true); setReloadToken((token) => token + 1); };

  const fields = useMemo(() => (
    extractGroups(schema)
      .filter((group) => !truthy(group.hideGroup))
      .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
      .flatMap((group) => group.fields ?? [])
      .filter((field) => field?.field && field.show !== 0 && !truthy(field.hidden))
      .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
  ), [schema]);

  // A form whose config never opted in shows no toggle at all, so every
  // existing search form behaves exactly as it did before.
  const booleanEnabled = Boolean(schema?.booleanSearchEnabled) && Boolean(schema?.booleanSearchEndpoint);
  const isBooleanMode = booleanEnabled && searchMode === 'boolean';

  // Only fields the backend actually parses as an expression may render as
  // one — see isBooleanParsable's doc comment. A field the admin marked
  // booleanCapable but mapped elsewhere (a config mistake, not a user
  // mistake) still renders traditionally; it just can't reach the broken
  // combination, so it's surfaced to whoever builds the form, not the person
  // searching.
  const booleanFields = useMemo(
    () => fields.filter((field) => field.booleanCapable && isBooleanParsable(field.booleanApiKey)),
    [fields],
  );

  useEffect(() => {
    fields
      .filter((field) => field.booleanCapable && !isBooleanParsable(field.booleanApiKey))
      .forEach((field) => {
        console.warn(
          `[SearchBuilderV1] "${field.field}" is marked booleanCapable but its booleanApiKey `
          + `(${JSON.stringify(field.booleanApiKey)}) is not one of ${BOOLEAN_PARSEABLE_API_KEYS.join(', ')} — `
          + 'the backend cannot parse it as an expression, so it will render as a normal field. Fix in Admin → Form Groups.',
        );
      });
  }, [fields]);

  const handleModeChange = (checked) => {
    const nextMode = checked ? 'boolean' : 'traditional';
    // A boolean-capable field holds a different value type in each mode (chips
    // vs. an expression string), so carrying values across would submit junk.
    const cleared = {};
    fields.forEach((field) => { if (field.booleanCapable) cleared[field.field] = undefined; });
    form.setFieldsValue(cleared);
    setFormValues((current) => ({ ...current, ...cleared }));
    setFieldErrors({});
    setSearchMode(nextMode);
  };

  const runSearch = useCallback(async (values, sourceOverride, paginationOverride = {}) => {
    const pageSize = Number(paginationOverride.pageSize) > 0
      ? Number(paginationOverride.pageSize) : pagination.pageSize;
    const requested = Number(paginationOverride.current);
    const current = Number.isFinite(requested) && requested > 0 ? Math.floor(requested) : 1;

    const checkedSources = getSearchSources(values?.selectedSource);
    const activeSource = sourceOverride
      ? resolveSearchSource(sourceOverride, selectedSource)
      : (checkedSources.includes('internal')
        ? 'internal'
        : resolveSearchSource(values?.selectedSource, selectedSource));

    setLastValues(values);
    setPagination({ current, pageSize });
    setFieldErrors({});
    setResults((prev) => ({ ...prev, loading: true, searched: true }));

    try {
      let result;
      if (isBooleanMode) {
        const body = buildBooleanRequest(values, fields, {
          defaultSourceType: schema?.booleanDefaultSourceType || activeSource,
        });
        result = await searchBoolean(schema.booleanSearchEndpoint, body, {
          limit: pageSize,
          offset: (current - 1) * pageSize,
        });
      } else {
        result = await searchModuleData(
          moduleName,
          { ...buildTraditionalParams(values, fields), selectedSource: activeSource },
          { limit: pageSize, offset: (current - 1) * pageSize },
          { scope: activeSource, filters: buildFilterConditions(values, fields) },
        );
      }

      setSelectedSource(activeSource);
      setResults({
        searched: true,
        rows: result.rows,
        total: result.total,
        fields: result.fields ?? [],
        actions: result.actions ?? [],
        actionRules: result.actionRules ?? [],
        loading: false,
      });
      setTabs((currentTabs) => buildSourceTabs({
        sources: result.sources?.length ? result.sources : checkedSources,
        activeSource,
        activeCount: result.total,
        responseCounts: result.counts,
        currentTabs,
        backendTabs: result.tabs,
      }));
    } catch (error) {
      // Point a rejected expression at the field that carries it when the
      // server names one; otherwise fall back to a form-level message.
      const details = error?.details ? String(error.details) : '';
      const blamed = error?.code === 'INVALID_BOOLEAN_QUERY'
        ? fields.find((field) => field.booleanCapable && details.toLowerCase().includes(String(field.field).toLowerCase()))
        : null;
      if (blamed) setFieldErrors({ [blamed.field]: details });
      else message.error(details || error?.message || 'Search failed');

      setResults({ ...EMPTY_RESULTS, searched: true });
    }
  }, [fields, isBooleanMode, moduleName, pagination.pageSize, schema, selectedSource]);

  const handleSearch = () => {
    // Each boolean-capable field carries its own blocking + warningOnly rule
    // (see the Form.Item below), so a malformed expression is rejected right
    // here by antd's own validation — no separate re-check needed.
    form.validateFields()
      .then((values) => runSearch(values, undefined, { current: 1 }))
      .catch(() => message.error('Fix the highlighted field before searching.'));
  };

  const handleReset = () => {
    form.resetFields();
    setFormValues({});
    setFieldErrors({});
    setResults(EMPTY_RESULTS);
    setTabs([]);
    setSelectedSource('internal');
  };

  const handleSourceAction = useCallback(async (sourceAction, record) => {
    const key = getSourceActionInstanceKey(sourceAction, record);
    setSourceActionState((current) => ({ ...current, [key]: { ...current[key], loading: true } }));
    try {
      await runSourceAction(sourceAction, record);
      setSourceActionState((current) => ({ ...current, [key]: { loading: false, completed: true } }));
      message.success(sourceAction.successMessage);
    } catch (error) {
      setSourceActionState((current) => ({ ...current, [key]: { loading: false } }));
      message.error(error?.message || sourceAction.errorMessage);
    }
  }, []);

  const resultFields = useMemo(() => {
    const base = results.fields?.length ? results.fields : (() => {
      const first = results.rows[0];
      if (!first) return [];
      return Object.keys(first)
        .filter((key) => !key.startsWith('_') && typeof first[key] !== 'object')
        .map((key) => ({
          value: key,
          field: key,
          label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase()),
          isVisible: true,
        }));
    })();

    const sourceActions = getAvailableSourceActions({ rows: results.rows, selectedSource });
    if (!sourceActions.length) return base;

    const actionFields = new Set(sourceActions.map((item) => item.field));
    return [
      ...base.filter((field) => field.cardRole !== 'action' && !actionFields.has(field.value)),
      ...sourceActions.map((sourceAction) => ({
        value: sourceAction.field,
        field: sourceAction.field,
        label: sourceAction.label ?? 'Action',
        cardRole: 'action',
        isVisible: true,
        sorter: false,
        render: (_value, record) => {
          if (!sourceActionAppliesToRecord(sourceAction, record, selectedSource)) return null;
          const key = getSourceActionInstanceKey(sourceAction, record);
          const state = sourceActionState[key] ?? {};
          return (
            <Button
              type="primary"
              size="small"
              loading={state.loading}
              disabled={!key || state.completed}
              onClick={() => handleSourceAction(sourceAction, record)}
            >
              {state.completed ? sourceAction.completedLabel : sourceAction.buttonLabel}
            </Button>
          );
        },
      })),
    ];
  }, [handleSourceAction, results.fields, results.rows, selectedSource, sourceActionState]);

  // The record context this search runs in (e.g. the job being sourced for).
  // Shared by row actions and the configured link button's URL template, so
  // the two can never disagree about which record they mean.
  const actionContext = useMemo(
    () => searchActionContext ?? (migrationJobId ? { jobId: migrationJobId } : null),
    [searchActionContext, migrationJobId],
  );

  const resultActions = useMemo(() => {
    if (!actionContext) return results.actions;
    const merge = (item) => ({
      ...item,
      popupState: { ...actionContext, ...item.popupState },
      ...(item.children ? { children: item.children.map(merge) } : {}),
    });
    return (results.actions ?? []).map(merge);
  }, [results.actions, actionContext]);

  // Admin-configured link button (schema.actionButton*). Off by default, so a
  // form that never enables it renders exactly as it does today.
  // safeNavTarget resolves :tokens AND rejects anything that isn't a plain
  // in-app path — an admin-supplied URL is never interpolated raw.
  const actionButtonEnabled = Boolean(schema?.actionButtonEnabled);
  const actionButtonTarget = actionButtonEnabled
    ? safeNavTarget(schema?.actionButtonUrl, undefined, actionContext ?? {})
    : null;

  const handleActionButton = () => {
    if (!actionButtonTarget) return;
    if (schema?.actionButtonTarget === '_blank') {
      window.open(actionButtonTarget, '_blank', 'noopener');
      return;
    }
    navigate(actionButtonTarget);
  };

  // Boolean mode mirrors the backend's own EMPTY_QUERY rule: at least one
  // boolean-capable field must carry a value — filling only Location or
  // Experience is not a boolean search. Traditional mode keeps the existing
  // "any criterion at all" rule.
  const hasBooleanCriteria = booleanFields.some((field) => {
    const value = formValues[field.field];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
  const searchDisabled = isBooleanMode ? !hasBooleanCriteria : !hasCriteria(formValues);
  const emptyBooleanHint = isBooleanMode && !hasBooleanCriteria && booleanFields.length
    ? `Enter a query in at least one of: ${booleanFields.map((field) => field.label ?? field.field).join(', ')}.`
    : '';

  if (!moduleName) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card"><Text type="danger">moduleName is required.</Text></Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card"><Skeleton active paragraph={{ rows: 4 }} /></Card>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card">
          <Space direction="vertical" align="center" style={{ width: '100%' }}>
            <Text strong>Failed to load the search form</Text>
            <Text type="secondary">{loadError}</Text>
            <Button icon={<ReloadOutlined />} onClick={retry}>Retry</Button>
          </Space>
        </Card>
      </div>
    );
  }

  if (!fields.length) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card">
          <Text type="secondary">No search fields are configured for “{moduleName}”.</Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="sbv1-page">
      <Card
        className="sbv1-card"
        title={<Text strong>{title ?? schema?.label ?? 'Search'}</Text>}
        extra={(
          <Space size={12}>
            {/* Every action lives up here in one row — nothing at the bottom of
                the field grid, so the controls never move as the form grows. */}
            <Button
              size="small"
              className="sbv1-action-btn"
              icon={<FilterOutlined />}
              onClick={handleReset}
            >
              Reset filters
            </Button>
            <Tooltip title={emptyBooleanHint || undefined}>
              <Button
                size="small"
                className="sbv1-action-btn"
                icon={<SearchOutlined />}
                disabled={searchDisabled}
                loading={results.loading}
                onClick={handleSearch}
              >
                Search
              </Button>
            </Tooltip>
            {actionButtonEnabled && (
              // Disabled rather than hidden when the URL is missing or unsafe,
              // so a half-finished config is visible instead of silently dead.
              <Tooltip title={actionButtonTarget ? undefined : 'No valid destination configured'}>
                <Button
                  size="small"
                  className="sbv1-action-btn"
                  icon={<FileTextOutlined />}
                  disabled={!actionButtonTarget}
                  onClick={handleActionButton}
                >
                  {schema?.actionButtonLabel || 'Open'}
                </Button>
              </Tooltip>
            )}
            {onBack && (
              <Button size="small" className="sbv1-action-btn" onClick={onBack}>Back</Button>
            )}
            {booleanEnabled && (
              <Tooltip title="Switch the marked fields to AND / OR / NOT expressions">
                <Space size={8}>
                  <Text type="secondary">Boolean</Text>
                  <Switch
                    size="small"
                    checked={searchMode === 'boolean'}
                    onChange={handleModeChange}
                  />
                </Space>
              </Tooltip>
            )}
          </Space>
        )}
      >
        <Form
          form={form}
          layout="vertical"
          colon={false}
          onValuesChange={(_changed, all) => setFormValues(all)}
        >
          <Row gutter={[24, 0]}>
            {fields.map((field) => {
              const asBoolean = isBooleanMode && booleanFields.includes(field);
              const rules = [];
              if (truthy(field.req)) {
                rules.push({ required: true, message: `${field.label ?? field.field} is required` });
              }
              if (asBoolean) {
                // Blocking: a broken expression (unbalanced brackets, a
                // dangling operator, "&&" instead of "AND") stops the search —
                // the Go parser would reject it too, so this just saves the
                // round trip.
                rules.push({
                  validator: (_rule, value) => {
                    const { errors } = validateBooleanQuery(value);
                    return errors.length ? Promise.reject(new Error(errors[0])) : Promise.resolve();
                  },
                });
                // warningOnly: legal but likely-wrong (all-exclusions, a bare
                // "*"/"?" the backend treats as a literal character, …) shows
                // as an amber hint and never blocks form.validateFields().
                rules.push({
                  warningOnly: true,
                  validator: (_rule, value) => {
                    const { warnings } = validateBooleanQuery(value);
                    return warnings.length ? Promise.reject(new Error(warnings[0])) : Promise.resolve();
                  },
                });
              }
              return (
                <Col xs={24} sm={12} lg={field.col ?? 12} key={field.field}>
                  <Form.Item
                    name={field.field}
                    label={field.label ?? field.field}
                    rules={rules.length ? rules : undefined}
                    initialValue={lockedInitialValue(field)}
                  >
                    {asBoolean
                      ? <BooleanQueryField field={field} serverError={fieldErrors[field.field]} />
                      : <SearchFieldControl field={field} moduleName={moduleName} />}
                  </Form.Item>
                </Col>
              );
            })}
          </Row>

          {/* The buttons moved to the card header; the hint stays down here
              next to the fields it is talking about. */}
          {emptyBooleanHint && (
            <div className="sbv1-actions">
              <Text type="secondary" className="sbv1-empty-hint">{emptyBooleanHint}</Text>
            </div>
          )}
        </Form>
      </Card>

      {results.searched && (
        <ListView
          moduleName={moduleName}
          breadcrumbItems={[]}
          dataSource={results.rows}
          fields={resultFields}
          total={results.total}
          loading={results.loading}
          current={pagination.current}
          pageSize={pagination.pageSize}
          showSearch={false}
          tabs={tabs}
          selectedSource={selectedSource}
          onTabChange={(key) => runSearch(lastValues ?? {}, key, { current: 1 })}
          onPageChange={(current, pageSize) => runSearch(lastValues ?? {}, selectedSource, { current, pageSize })}
          onPageSizeChange={(current, pageSize) => runSearch(lastValues ?? {}, selectedSource, { current: 1, pageSize })}
          actions={resultActions}
          actionRules={results.actionRules}
        />
      )}
    </div>
  );
}

SearchBuilderV1.propTypes = {
  moduleName: PropTypes.string.isRequired,
  action: PropTypes.string,
  formType: PropTypes.string,
  title: PropTypes.string,
  migrationJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  searchActionContext: PropTypes.object,
  onBack: PropTypes.func,
};
